import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { Dispatch, type ReactNode, SetStateAction, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface FilterProviderProps {
  children: ReactNode;
}

interface FilterContextProps {
  onFilterChange: (filterName: string, updatedFilter: string) => void;
  setFilters: Dispatch<SetStateAction<string[]>>;
  setFiltersUpdaters: Dispatch<SetStateAction<Record<string, any>>>;
  filtersModalMode: () => void;
  filtersPageMode: () => void;
}

const FilterContext = createContext<FilterContextProps | undefined>(undefined);

export const FilterProvider: React.FC<FilterProviderProps> = (props: FilterProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keepFilters, setKeepFilters] = useState(true);
  const [showParams, setShowParams] = useState(true);
  const [filters, setFilters] = useState<string[]>([]);
  const [filtersValues, setFiltersValues] = useState<Record<string, string | undefined>>({});
  const [filtersUpdaters, setFiltersUpdaters] = useState<Record<string, any>>({});

  const onFilterChange = useCallback(
    (filterName: string, updatedFilter?: any) => {
      if (!updatedFilter) {
        setFiltersValues((prev) => ({ ...prev, [filterName]: undefined }));
        if (filterName in filtersUpdaters) filtersUpdaters[filterName](undefined);
        searchParams.delete(filterName);
      } else {
        let updatedFilterStr = updatedFilter;
        if (updatedFilterStr instanceof Date) updatedFilterStr = readableDate(updatedFilterStr, 'MMM-d-yyyy');

        if (typeof updatedFilterStr == 'object') {
          if (updatedFilterStr.from) updatedFilterStr.from = readableDate(updatedFilterStr.from, 'MMM-d-yyyy');

          if (updatedFilterStr.to) updatedFilterStr.to = readableDate(updatedFilterStr.to, 'MMM-d-yyyy');

          const { from, to } = updatedFilterStr;
          updatedFilterStr = `from.${from}_to.${to ?? ''}`;
        }
        setFiltersValues((prev) => ({ ...prev, [filterName]: updatedFilterStr }));
        if (filterName in filtersUpdaters) filtersUpdaters[filterName](updatedFilterStr);
        searchParams.set(filterName, updatedFilterStr);
      }
      if (showParams) setSearchParams(searchParams);
      else setSearchParams({});
    },
    [filtersUpdaters, searchParams, setSearchParams, showParams]
  );

  const resetFilters = useCallback(() => {
    filters.forEach((e) => {
      onFilterChange(e, '');
    });
  }, [filters, onFilterChange]);

  const filtersPageMode = useCallback(() => {
    setShowParams(true);
    if (keepFilters) setKeepFilters(false);
    else resetFilters();
  }, [keepFilters, resetFilters]);

  const filtersModalMode = useCallback(() => {
    setShowParams(false);
    setKeepFilters(true);
  }, []);

  useEffect(() => {
    filters.forEach((f) => {
      if (f in filtersValues) onFilterChange(f, filtersValues[f]);
      if (searchParams.has(f)) onFilterChange(f, searchParams.get(f));
    });
    searchParams.forEach((_, key) => {
      if (filters.length && !filters.includes(key)) onFilterChange(key, null);
    });
  }, [filters]); // eslint-disable-line

  return (
    <FilterContext.Provider
      value={{
        onFilterChange,
        setFilters,
        setFiltersUpdaters,
        filtersModalMode,
        filtersPageMode
      }}>
      {props.children}
    </FilterContext.Provider>
  );
};

export const useFilter = () => {
  const context = useContext(FilterContext);
  if (context == null) throw new Error('useFilter must be used inside the FilterProvider node.');

  return context;
};
